// @ts-nocheck: converted from JS

import { type FocusEventHandler, type ReactNode, type JSX } from 'react';
import ReactSelect, {
  type GetOptionLabel,
  type GetOptionValue,
  type GroupBase,
  type MultiValue,
  type OptionsOrGroups,
  type SingleValue,
  type StylesConfig,
} from 'react-select';
import { type SelectComponents } from 'react-select/dist/declarations/src/components';
import { type FilterOptionOption } from 'react-select/dist/declarations/src/filters';

interface BaseSelectProps<T> {
  className?: string;
  defaultValue?: T;
  options?: OptionsOrGroups<T, GroupBase<T>>;
  customStyles?: unknown;
  isSearchable?: boolean;
  isClearable?: boolean;
  onBlur?: FocusEventHandler;
  components?: Partial<SelectComponents<T, boolean, GroupBase<T>>>;
  getOptionLabel?: GetOptionLabel<T>;
  getOptionValue?: GetOptionValue<T>;
  isOptionDisabled?: (option: T) => boolean;
  isLoading?: boolean;
  value?: unknown;
  disabled?: boolean;
  filterOption?: (option: FilterOptionOption<T>, inputValue: string) => boolean;
  placeholder?: string;
  name?: string;
  'aria-label'?: string;
  /**
   * Portal target for the dropdown menu. When set, the menu is rendered in the portal,
   * `menuPortal` z-index styling is applied, and `menuPosition` defaults to `"fixed"`
   * unless you pass `menuPosition` explicitly.
   */
  menuPortalTarget?: HTMLElement | null;
  /** React-select menu position; defaults to `"fixed"` when `menuPortalTarget` is set. */
  menuPosition?: 'absolute' | 'fixed';
}

interface SingleSelectProps<T> extends BaseSelectProps<T> {
  onChange: (newValue: SingleValue<T>) => void;
  isMulti: false;
}

interface MultiSelectProps<T> extends BaseSelectProps<T> {
  onChange: (newValue: MultiValue<T>) => void;
  isMulti: true;
}

type SelectProps<T> = SingleSelectProps<T> | MultiSelectProps<T>;

export interface SelectOption<T> {
  value: T;
  label: string;
}

const MENU_PORTAL_Z_INDEX = 9999;

const Select = <T extends object>({
  className = '',
  defaultValue,
  options,
  onChange,
  isMulti = false,
  customStyles,
  isSearchable,
  isClearable,
  onBlur,
  components,
  getOptionLabel,
  getOptionValue,
  value,
  disabled,
  isOptionDisabled,
  isLoading,
  filterOption,
  placeholder,
  name,
  'aria-label': ariaLabel,
  menuPortalTarget,
  menuPosition,
}: SelectProps<T>): JSX.Element => {
  const resolvedMenuPosition = menuPosition ?? (menuPortalTarget != null ? 'fixed' : undefined);

  const styles: StylesConfig<ReactNode, boolean, GroupBase<ReactNode>> = {
    control: (base) => ({
      ...base,
      height: 40,
      minHeight: 40,
    }),
    ...(menuPortalTarget != null
      ? {
          menuPortal: (base) => ({ ...base, zIndex: MENU_PORTAL_Z_INDEX }),
        }
      : {}),
  };

  return (
    <ReactSelect
      className={`w5 ui__select f6 ${className}`}
      classNamePrefix="ui__select"
      defaultValue={defaultValue}
      options={options}
      isMulti={isMulti}
      onChange={onChange}
      styles={{ ...styles, ...customStyles }}
      isSearchable={isSearchable}
      isClearable={isClearable}
      onBlur={onBlur}
      name={name}
      components={components}
      isOptionDisabled={isOptionDisabled}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      value={value}
      filterOption={filterOption}
      isDisabled={disabled}
      isLoading={isLoading}
      placeholder={placeholder}
      aria-label={ariaLabel}
      menuPortalTarget={menuPortalTarget ?? undefined}
      menuPosition={resolvedMenuPosition}
    />
  );
};

export default Select;
